import { useState } from "react";
import type { ReactNode } from "react";
import Uppy from "@uppy/core";
import { DashboardModal } from "@uppy/react";
import "@uppy/core/dist/style.min.css";
import "@uppy/dashboard/dist/style.min.css";
import XHRUpload from "@uppy/xhr-upload";
import type { UploadResult } from "@uppy/core";
import { Button } from "@/components/ui/button";

interface ObjectUploaderProps {
  maxNumberOfFiles?: number;
  maxFileSize?: number;
  uploadType?: string; // Add uploadType prop for proxy endpoint
  onComplete?: (
    result: UploadResult<Record<string, unknown>, Record<string, unknown>>
  ) => void;
  buttonClassName?: string;
  children: ReactNode;
}

/**
 * A file upload component that renders as a button and provides a modal interface for
 * file management.
 * 
 * Features:
 * - Renders as a customizable button that opens a file upload modal
 * - Provides a modal interface for:
 *   - File selection
 *   - File preview
 *   - Upload progress tracking
 *   - Upload status display
 * 
 * The component uses Uppy under the hood to handle all file upload functionality.
 * All file management features are automatically handled by the Uppy dashboard modal.
 * 
 * @param props - Component props
 * @param props.maxNumberOfFiles - Maximum number of files allowed to be uploaded
 *   (default: 1)
 * @param props.maxFileSize - Maximum file size in bytes (default: 10MB)
 * @param props.uploadType - Type of upload (e.g., 'profile_picture') for storage limit exemption
 * @param props.onComplete - Callback function called when upload is complete. Receives
 *   the upload result with the uploadURL and other metadata.
 * @param props.buttonClassName - Optional CSS class name for the button
 * @param props.children - Content to be rendered inside the button
 */
export function ObjectUploader({
  maxNumberOfFiles = 1,
  maxFileSize = 10485760, // 10MB default
  uploadType = 'general',
  onComplete,
  buttonClassName,
  children,
}: ObjectUploaderProps) {
  const [showModal, setShowModal] = useState(false);
  const [uppy] = useState(() => {
    const uppyInstance = new Uppy({
      restrictions: {
        maxNumberOfFiles,
        maxFileSize,
        allowedFileTypes: ['image/*'], // Only allow images for profile pictures
      },
      autoProceed: false,
      // Mobile-friendly configuration
      meta: {
        // Add mobile-friendly metadata
        source: 'mobile-upload'
      }
    });

    // Use XHRUpload plugin to send files to proxy endpoint
    uppyInstance
      .use(XHRUpload, {
        endpoint: '/api/objects/upload/proxy',
        method: 'POST',
        formData: true, // Send as FormData
        fieldName: 'file', // Field name for the file
        withCredentials: true, // Include credentials for authenticated requests
        // Add uploadType to all uploads
        allowedMetaFields: ['uploadType'] as any, // Type assertion for Uppy XHR upload metadata fields
        getResponseData: (xhr: XMLHttpRequest) => {
          try {
            const responseData = JSON.parse(xhr.responseText);
            if (responseData.success) {
              return {
                uploadURL: responseData.uploadURL,
                objectId: responseData.objectId,
                path: responseData.path
              };
            } else {
              throw new Error(responseData.error || 'Upload failed');
            }
          } catch (error) {
            console.error('Failed to parse upload response:', error);
            throw error;
          }
        },
      } as any) // Type assertion for XHR upload options
      .on('file-added', (file) => {
        // Set uploadType for this file
        uppyInstance.setFileMeta(file.id, {
          uploadType: uploadType
        } as any); // Type assertion for custom meta fields
      })
      .on("complete", (result) => {
        onComplete?.(result);
      })
      .on("error", (error) => {
        console.error("Upload error:", error);
      });

    return uppyInstance;
  });

  return (
    <div>
      <Button onClick={() => setShowModal(true)} className={buttonClassName}>
        {children}
      </Button>

      <DashboardModal
        uppy={uppy}
        open={showModal}
        onRequestClose={() => setShowModal(false)}
        proudlyDisplayPoweredByUppy={false}
        // Mobile-friendly configuration
        closeModalOnClickOutside={true}
        animateOpenClose={true}
        browserBackButtonClose={true}
        // Better mobile experience
        theme="light"
      />
    </div>
  );
}